import TextButton from 'LuxKit/Button/TextButton'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import useOffer from 'hooks/Offers/useOffer'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import useAccommodationMapSearchPath from 'hooks/Search/useAccommodationMapSearchPath'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import { nonNullable } from 'lib/array/arrayUtils'
import { getVillaCapacityText } from 'lib/homesAndVillas/getVillaCapacityText'
import {
  buildSearchParamsKey,
  getMinimumDurationFromFlexibleNights,
  getShouldUseSearchPrices,
} from 'lib/search/searchUtils'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import HotelSearchTilePriceStack from './HotelSearchTilePriceStack'
import VillaSearchTileFeatures from './VillaSearchTileFeatures'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import { generateLoyaltyPointsCalculatorVillasOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import HotelSearchTileUnavailabilityBanner
  from 'components/SearchKit/SearchTiles/accommodations/HotelSearchTileUnavailabilityBanner'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import CurrencyContext from 'contexts/currencyContext'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import Group from 'components/utils/Group'
import { EmptyObject } from 'lib/object/objectUtils'

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
  filters?: App.OfferListFilters
  showDescription?: boolean
  position?: number
  eagerLoadFirstImage?: boolean
}

function VillaSearchTile({ offer, filters, showDescription = false, position, eagerLoadFirstImage }: Props) {
  const intl = useIntl()
  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const metaData = useOfferMetaData(offer.id, filters)
  const checkIn = filters?.checkIn ?? metaData?.suggestedTravelDates?.checkIn
  const checkOut = filters?.checkOut ?? metaData?.suggestedTravelDates?.checkOut
  const isSpecificSearch = !!(checkIn && checkOut && filters?.rooms)

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)

  const [, fetchingOffer] = useOffer<App.Offer>(offer.id, {
    requireSummaryOnly: !isSpecificSearch,
  })

  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, filters?.rooms), [checkIn, checkOut, filters?.rooms])
  const filtersForBestPrice = useMemo<App.OfferListFilters>(() => ({
    ...(filters ?? EmptyObject),
    checkIn,
    checkOut,
  }), [filters, checkIn, checkOut])

  // Hydrate best-price data on the list view just like the split-map path does.
  // This lets tiles fall back to live rate/member pricing when search metadata is incomplete.
  useOffersBestPrices(offer, filtersForBestPrice)

  const bestMaybeAvailableRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const bestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (!shouldUseRealSearchPrices) {
      if (!isSpecificSearch || bestPriceError) {
        return offer.lowestPricePackage
      }
      if (bestMaybeAvailableRate?.available && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      // use search pricing package id by default
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      const duration = metaData?.pricing?.duration ?? offer.lowestPricePackage?.duration
      if (!pkgId || !duration || !bestMaybeAvailableRate?.rate?.roomRateId) {
        return offer.lowestPricePackage
      }

      const pkg = offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, bestMaybeAvailableRate.rate.roomRateId))
      return pkg ?? offer.lowestPricePackage
    }
  }, [shouldUseRealSearchPrices, isSpecificSearch, bestPriceError, bestMaybeAvailableRate, fetchingOffer, offer, metaData])
  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type

  const locations = useMemo<Array<string>>(() => nonNullable([offer.locationHeading, offer.locationSubheading]), [offer])
  const mapSearchPath = useAccommodationMapSearchPath()
  const mapUrl = useMapSearchOfferUrl(false, offer, filters, mapSearchPath)

  const capacityText = useMemo(() => getVillaCapacityText(intl, offer), [intl, offer])

  const bestAvailableRate = bestMaybeAvailableRate?.available ? bestMaybeAvailableRate.rate : undefined
  const duration = filters?.flexibleMonths && filters.flexibleNights ? getMinimumDurationFromFlexibleNights(filters.flexibleNights as FLEXIBLE_DURATION_RANGE) : offer.lowestPricePackage?.duration
  const {
    defaultPricing,
    memberPricing,
  } = useSearchPrices({
    offer,
    pkg: bestPackage,
    rate: bestAvailableRate,
    duration,
    filters,
    useRealSearchPrices: shouldUseRealSearchPrices,
  })

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    const propertyFees = defaultPricing.fees?.find((fee) => fee.type === 'property')?.amount ?? 0
    return [
      generateLoyaltyPointsCalculatorVillasOptions(offer, {
        price: defaultPricing.price,
        memberPrice: memberPricing?.price,
        pkg: bestPackage,
        propertyFees,
        nights: bestPackage?.duration,
      }),
    ]
  }, [bestPackage, defaultPricing.price, memberPricing?.price, offer, defaultPricing.fees])

  const burnResult = useSearchBurnPoints(defaultPricing.price, getLoyaltyProductType(offer) ?? 'hotel_flash', getHotelEncryptedMargin(offer.type, bestPackage))

  const hidePrice = useHideAnytimePrices(offer.type, metaData?.hidePricing, offer.productType, filters)
  const unavailable = !metaData?.available

  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const currency = useContext(CurrencyContext)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit =
    isTasteOfGoldActive &&
    isTasteOfGoldEligibleOffer(offer, currency, offer.property.geoData.countryCode?.toLowerCase() !== 'au', defaultPricing.price)

  const handleSelectDatesClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onAnyHotelSelectDatesPrompt) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  return <SearchTile
    banner={<>
      {showDescription && filters && (
        <OfferDescriptionContainer
          filters={filters}
          offerId={offer.id}
          position={position}
        />
      )}
      {offerSiteTakeover?.searchPage?.tileBanner}
    </>}
    action={
      !unavailable && <>
        {hidePrice && (<TextButton kind="primary" onClick={handleSelectDatesClick}>{intl.formatMessage({
          id: 'uGzbZh9hyDitzQbINePr',
          defaultMessage: 'Select dates',
          description: 'Button text prompting user to select dates on villa search tile',
        })}
        </TextButton>)}
        {!hidePrice && (<TextButton nonInteractable kind="primary">{intl.formatMessage({
          id: '2dhA1o-Ugt0THCI36jtf',
          defaultMessage: 'View offer',
          description: 'CTA button on villa search tile to view offer details',
        })}
        </TextButton>)}
      </>
    }
    bookmarkAction={<BookmarkButton offer={offer} />}
    eagerLoadFirstImage={eagerLoadFirstImage}
    cancellationPolicyLabel={!!cancellationPolicyType && <TileCancellationPolicy
      cancellationPolicyType={cancellationPolicyType}
      checkInDate={filters?.checkIn}
      timezoneOffset={offer?.property?.timezoneOffset}
      offerType={offer.type}
    />}
    features={<VillaSearchTileFeatures offer={offer} pkg={bestPackage} />}
    images={offer.images}
    overline={<SearchTileRowOverlineLocation locations={locations} mapUrl={mapUrl} />}
    pricePoint={!unavailable && <HotelSearchTilePriceStack
      offer={offer}
      filters={filters}
      bestPackage={bestPackage}
      bestRate={bestAvailableRate}
      hidePrice={hidePrice}
    />}
    rating={isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) ? offer.property.rating : undefined}
    subtitle={capacityText}
    title={offer.property.name}
    meta={<Group direction="vertical" gap={8} noDisplayWhenEmpty>
      <LoyaltyPointsEarn
        calculationRequests={pointsEarnCalculationRequests}
        calculationType="estimate"
        pointsToApply={burnResult.enabled ? burnResult.pointsToBurn : undefined}
        burnPriceInPoints={burnResult.enabled ? burnResult.priceInPoints : undefined}
      />
      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
    </Group>}
    offer={offer}
    bottomExtension={unavailable &&
      <HotelSearchTileUnavailabilityBanner
        offer={offer}
        unavailableReason={metaData?.unavailableReason}
        filters={filters}
      />}
    labels={<ContentTagLabels offerId={offer.id} page="search" />}
  />
}

export default VillaSearchTile
